import React, { useState } from 'react';
import {
  Box,
  Collapse,
  colors,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { EquipmentCategoryItem } from 'api/equipmentApi';
import { RateType } from 'api/consumablesApi';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { currencyType } from 'api/organizationApi';

interface RateRow {
  branchId: string | number;
  branch: string;
  client: string;
  price: number;
  isDefaultPrice: boolean;
  effectiveFrom: Date;
}

interface FilteredEquipmentRateTableProps {
  sellTableRows?: Partial<EquipmentCategoryItem>;
  buyTableRows?: Partial<EquipmentCategoryItem>;
  openSellRateForm: boolean;
  openBuyRateForm: boolean;
}

const getRateRows = (
  type: RateType,
  tableRows?: Partial<EquipmentCategoryItem>
): RateRow[] => {
  if (!tableRows) return [];
  const rates =
    type === RateType.SellRate ? tableRows.sellRates : tableRows.buyRates;
  if (!rates || !Array.isArray(rates)) return [];

  return rates.map((item: any) => ({
    branchId: item.branchId,
    branch: item.branch?.name || item.client?.legalName || '-',
    isDefaultPrice: item.isDefault,
    client: item.client?.legalName || '-',
    price: Number(type === RateType.SellRate ? item.sellRate : item.buyRate),
    effectiveFrom: item.effectiveFrom,
  }));
};

const FilteredEquipmentRateTable: React.FC<FilteredEquipmentRateTableProps> = ({
  sellTableRows,
  buyTableRows,
  openSellRateForm,
  openBuyRateForm,
}) => {
  const [open, setOpen] = useState(false);
  const getActiveRates = () => {
    if (openSellRateForm) return getRateRows(RateType.SellRate, sellTableRows);
    if (openBuyRateForm) return getRateRows(RateType.BuyRate, buyTableRows);
    return [];
  };

  const rowsToDisplay = getActiveRates();

  const columns = [
    { label: 'Branch', show: true },
    { label: 'Client', show: openSellRateForm },
    { label: 'Price', show: true },
    { label: 'Effective From', show: true },
  ];
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: colors.blue[50],
          padding: '0.5rem 1rem',
        }}>
        <Typography variant="subtitle2" sx={{ pl: 0.1 }}>
          Existing Rates
        </Typography>
        <IconButton
          size="medium"
          aria-label={open ? 'Collapse history' : 'Expand history'}
          onClick={() => setOpen((prev) => !prev)}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse
        in={open}
        timeout="auto"
        unmountOnExit
        sx={{ backgroundColor: colors.grey[50], padding: '0.8rem' }}>
        <TableContainer>
          <Table sx={{ width: '100%' }} aria-label="consumable rates table">
            <TableHead>
              <TableRow>
                {columns
                  .filter((col) => col.show)
                  .map((col) => (
                    <TableCell key={col.label}>{col.label}</TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowsToDisplay.length > 0 ? (
                rowsToDisplay.map((row, index) => (
                  <TableRow
                    key={row.branchId ? String(row.branchId) : `row-${index}`}>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {row.branchId
                        ? row.branch
                        : row.isDefaultPrice
                        ? 'Default Price'
                        : '-'}
                    </TableCell>
                    {openSellRateForm && (
                      <TableCell sx={{ borderBottom: 'none' }}>
                        {row?.client}
                      </TableCell>
                    )}
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {currencyType}
                      {row.price}
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {new Date(row.effectiveFrom).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.filter((col) => col.show).length}
                    align="center">
                    No rates available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Grid>
  );
};

export default FilteredEquipmentRateTable;
